import { getDistance } from '@shared/helpers/geometry/coordinates.helpers';
import {
  isEqualPoints,
  projectPointOntoSegmentClamped,
} from '@shared/helpers/geometry/geometry.helpers';
import type { Point2D } from '@shared/models/geometry.model';
import { SnapCandidateType } from '../models/snap.models';

export type BuildingSolidOutlineSnap = {
  position: Point2D;
  snapType: SnapCandidateType.Point | SnapCandidateType.Line;
};

export type SnapPositionToBuildingSolidOutlinesInput = {
  /** Pointer position in metric space, before snapping */
  position: Point2D;
  /** Solid footprints to snap against (typically sibling buildings at this height) */
  shapes: readonly { points?: readonly Point2D[] }[];
  /** Max distance to a vertex that still counts as a snap */
  pointThreshold: number;
  /** Max distance to an edge that still counts as a snap */
  lineThreshold: number;
};

/**
 * Snap {@link SnapPositionToBuildingSolidOutlinesInput.position} to the closest
 * point on sibling solid outlines.
 *
 * The geometrically nearest outline point wins, so a wall the pointer is
 * sliding along is not stolen by a corner that is still inside
 * {@link SnapPositionToBuildingSolidOutlinesInput.pointThreshold}. A vertex is
 * used only when it is that nearest outline point (exact coincident corners).
 */
export const snapPositionToBuildingSolidOutlines = (
  input: SnapPositionToBuildingSolidOutlinesInput,
): BuildingSolidOutlineSnap | undefined => {
  const { position, shapes } = input;
  if (!shapes.length) return undefined;

  const threshold = Math.max(input.pointThreshold, input.lineThreshold);
  let closest: Point2D | undefined;
  let closestDistance = threshold;

  for (const shape of shapes) {
    const points = shape.points ?? [];
    if (points.length < 2) continue;
    const total = points.length;
    for (let i = 0; i < total; i++) {
      const start = points[i];
      const end = points[(i + 1) % total];
      if (!start || !end) continue;
      const projected = projectPointOntoSegmentClamped(position, start, end);
      const distance = getDistance(position, projected);
      if (distance >= closestDistance) continue;
      closest = projected;
      closestDistance = distance;
    }
  }

  if (!closest) return undefined;

  for (const shape of shapes) {
    for (const point of shape.points ?? []) {
      if (!isEqualPoints(closest, point)) continue;
      return {
        position: point,
        snapType: SnapCandidateType.Point,
      };
    }
  }

  return {
    position: closest,
    snapType: SnapCandidateType.Line,
  };
};
